/**
 * DeathTriggerSystem — conditional effects that fire when enemies die.
 *
 * Registers triggers with configurable conditions (status requirements,
 * kill-by-skill, kill-by-status, zone presence, timing windows) and
 * effects (explosions, zone spawns, status propagation, pulls, XP grants).
 * Multiple trees reference death-reactive behaviors — Fire's death-burst,
 * Poison's corpse clouds, Blades' bleed-stack XP, Specter's Blood Price.
 *
 * The system evaluates conditions against a DeathContext abstraction,
 * keeping it decoupled from Phaser's entity system. Matching triggers
 * produce DeathTriggerEvent records that the caller (EnemyManager,
 * CombatSystem) translates into game-world effects.
 *
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Information about a dying enemy, provided by the caller. */
export interface DeathContext {
  /** Which skill dealt the killing blow (undefined if killed by status/environment). */
  killedBySkillId?: string;
  /** Which status effect dealt the killing blow (undefined if killed by direct damage). */
  killedByStatusId?: string;
  /** World position of the death. */
  deathPosition: { x: number; y: number };
  /** Status effect IDs currently active on the dying enemy. */
  activeStatuses: string[];
  /** Zone type IDs the enemy is currently inside. */
  activeZones: string[];
  /** Per-status intensity on the dying enemy, for scaling effects. */
  statusIntensities?: Map<string, number>;
  /** Per-status stack count on the dying enemy, for stack-based effects. */
  statusStacks?: Map<string, number>;
  /** Simulation time of death in ms, for requireWithinMs checks. */
  deathTimeMs?: number;
  /** Time of last status application in ms, for requireWithinMs checks. */
  lastApplicationTimeMs?: number;
}

export interface ExplosionEffect {
  readonly type: 'explosion';
  readonly damage: number;
  readonly radiusTiles: number;
  readonly statusToApply?: string;
  readonly statusDurationMs?: number;
}

export interface SpawnZoneEffect {
  readonly type: 'spawnZone';
  readonly zoneType: string;
  readonly radiusTiles: number;
  readonly durationMs: number;
}

export interface PropagateStatusEffect {
  readonly type: 'propagateStatus';
  readonly statusId: string;
  readonly rangeTiles: number;
  readonly intensityMultiplier?: number;
}

export interface PullEffect {
  readonly type: 'pull';
  readonly rangeTiles: number;
  readonly distanceTiles: number;
}

export interface GrantXpEffect {
  readonly type: 'grantXp';
  readonly baseAmount: number;
  readonly perBleedStackMultiplier?: number;
  readonly perKillMultiplier?: number;
}

export type DeathEffect =
  | ExplosionEffect
  | SpawnZoneEffect
  | PropagateStatusEffect
  | PullEffect
  | GrantXpEffect;

export interface DeathTriggerConfig {
  readonly id: string;
  readonly sourceSkillId: string;

  // Conditions (at least one, all specified must be true)
  readonly requireStatus?: string;
  readonly requireKillBySkill?: string | readonly string[];
  readonly requireKillByStatus?: string;
  readonly requireInZone?: string;
  readonly requireWithinMs?: number;

  // Effects (can combine)
  readonly explosion?: Omit<ExplosionEffect, 'type'>;
  readonly spawnZone?: Omit<SpawnZoneEffect, 'type'>;
  readonly propagateStatus?: Omit<PropagateStatusEffect, 'type'>;
  readonly pull?: Omit<PullEffect, 'type'>;
  readonly grantXp?: Omit<GrantXpEffect, 'type'>;
}

/** Emitted when a death trigger fires. */
export interface DeathTriggerEvent {
  readonly triggerId: string;
  readonly sourceSkillId: string;
  readonly deathPosition: { x: number; y: number };
  readonly effect: DeathEffect;
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class DeathTriggerSystem {
  private readonly triggers = new Map<string, DeathTriggerConfig>();

  /** Register a death trigger. Overwrites any existing trigger with the same ID. */
  register(config: DeathTriggerConfig): void {
    this.triggers.set(config.id, config);
  }

  /** Unregister a trigger (on respec). */
  unregister(triggerId: string): void {
    this.triggers.delete(triggerId);
  }

  /**
   * Called when an enemy dies. Checks all registered triggers against
   * the death context and returns events for those whose conditions match.
   */
  onEnemyDeath(context: DeathContext): DeathTriggerEvent[] {
    const events: DeathTriggerEvent[] = [];

    for (const trigger of this.triggers.values()) {
      if (!this.conditionsMatch(trigger, context)) continue;
      this.collectEffects(trigger, context, events);
    }

    return events;
  }

  // -----------------------------------------------------------------------
  // Internal
  // -----------------------------------------------------------------------

  private conditionsMatch(trigger: DeathTriggerConfig, context: DeathContext): boolean {
    if (trigger.requireStatus !== undefined) {
      if (!context.activeStatuses.includes(trigger.requireStatus)) return false;
    }

    if (trigger.requireKillBySkill !== undefined) {
      const allowed = Array.isArray(trigger.requireKillBySkill)
        ? trigger.requireKillBySkill
        : [trigger.requireKillBySkill];
      if (!allowed.includes(context.killedBySkillId)) return false;
    }

    if (trigger.requireKillByStatus !== undefined) {
      if (context.killedByStatusId !== trigger.requireKillByStatus) return false;
    }

    if (trigger.requireInZone !== undefined) {
      if (!context.activeZones.includes(trigger.requireInZone)) return false;
    }

    if (trigger.requireWithinMs !== undefined) {
      if (context.deathTimeMs === undefined || context.lastApplicationTimeMs === undefined) {
        return false;
      }
      const elapsed = context.deathTimeMs - context.lastApplicationTimeMs;
      if (elapsed > trigger.requireWithinMs) return false;
    }

    return true;
  }

  private collectEffects(
    trigger: DeathTriggerConfig,
    context: DeathContext,
    events: DeathTriggerEvent[],
  ): void {
    const base = {
      triggerId: trigger.id,
      sourceSkillId: trigger.sourceSkillId,
      deathPosition: context.deathPosition,
    };

    if (trigger.explosion) {
      events.push({ ...base, effect: { type: 'explosion', ...trigger.explosion } });
    }

    if (trigger.spawnZone) {
      events.push({ ...base, effect: { type: 'spawnZone', ...trigger.spawnZone } });
    }

    if (trigger.propagateStatus) {
      events.push({ ...base, effect: { type: 'propagateStatus', ...trigger.propagateStatus } });
    }

    if (trigger.pull) {
      events.push({ ...base, effect: { type: 'pull', ...trigger.pull } });
    }

    if (trigger.grantXp) {
      events.push({ ...base, effect: { type: 'grantXp', ...trigger.grantXp } });
    }
  }
}
